import { Plus, Trash2, Copy, ExternalLink, Palette } from 'lucide-react';
import DashboardInsightsCards from '@/components/dashboard/DashboardInsightsCards';
import DashboardBillingCard from '@/components/dashboard/DashboardBillingCard';
import DashboardChatbotRuntimeCard from '@/components/dashboard/DashboardChatbotRuntimeCard';
import WidgetCustomizerDialog from '@/components/dashboard/WidgetCustomizerDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';
import {
  FieldLabel,
  visiblePlanTiers,
  CRM_PROVIDERS,
  WEEK_DAYS,
  TIMEZONE_OPTIONS,
  SITE_LANGUAGE_OPTIONS,
  PRICE_CURRENCIES,
  todayLocalIso,
  type CrmProvider,
} from '@/pages/dashboard/dashboardShared';

function useD() {
  return useDashboardSite();
}

export function DashboardSitePickerCard() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Site</CardTitle>
          <CardDescription>Choose which business/site you want to configure.</CardDescription>
        </CardHeader>
        <CardContent>
          <select
            className="w-full rounded-md border border-input bg-background py-2 pl-2 pr-8 text-foreground"
            value={selectedSiteId ?? ''}
            onChange={(e) => setSelectedSiteId(Number(e.target.value))}
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name} ({site.role})
              </option>
            ))}
          </select>
          {config ? (
            <div className="mt-4 space-y-2">
              <FieldLabel
                text="Default language"
                hint="Used for the chat greeting and customer emails (confirmation, reminder, feedback). Defaults to English."
              />
              <select
                className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50 md:max-w-sm"
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                disabled={!canEdit}
              >
                {SITE_LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {!canEdit && config ? (
            <p className="mt-2 text-sm text-amber-500">
              You have view-only access to this site. Ask an admin for editor access to make changes.
            </p>
          ) : null}
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardEmbedPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      {embedInfo ? (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle>Embed Chat Widget</CardTitle>
              <CardDescription>Add the assistant to your website with this site ID and snippet.</CardDescription>
            </div>
            {canCustomizeWidget ? (
              <Button type="button" size="sm" onClick={() => setIsWidgetCustomizerOpen(true)}>
                <Palette className="mr-1.5 h-4 w-4" /> Customize appearance
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-4">
            {!canCustomizeWidget && !isPlatformAdmin ? (
              <p className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
                Widget appearance customization isn&apos;t enabled for this site yet. Ask a platform admin to turn it on.
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel text="Website ID" hint="Use this value as data-website-id in the embed script and website_id in chat API calls." />
                <div className="flex gap-2">
                  <Input value={String(embedInfo.website_id)} readOnly />
                  <Button type="button" variant="outline" size="icon" onClick={() => copyText('Website ID', String(embedInfo.website_id))}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <FieldLabel text="API base URL" hint="Your public API host where /static/chatbot.js and /chat-ui are served." />
                <div className="flex gap-2">
                  <Input value={embedInfo.api_base_url} readOnly />
                  <Button type="button" variant="outline" size="icon" onClick={() => copyText('API base URL', embedInfo.api_base_url)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel text="Script embed (recommended)" hint="Paste before </body> on your site. Loads the floating chat button + iframe widget." />
              <div className="flex gap-2">
                <Textarea rows={3} value={embedInfo.script_snippet} readOnly className="font-mono text-xs" />
                <Button type="button" variant="outline" size="icon" onClick={() => copyText('Script snippet', embedInfo.script_snippet)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel text="Iframe embed (optional)" hint="Embed the chat panel directly on a page without the floating launcher button." />
              <div className="flex gap-2">
                <Textarea rows={3} value={embedInfo.iframe_snippet} readOnly className="font-mono text-xs" />
                <Button type="button" variant="outline" size="icon" onClick={() => copyText('Iframe snippet', embedInfo.iframe_snippet)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" size="sm">
                <a href={embedInfo.chat_ui_test_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-1 h-4 w-4" /> Open test chat page
                </a>
              </Button>
              {isPlatformAdmin && embedInfo.notes.internal_api_key_hint ? (
                <p className="text-xs text-muted-foreground">{embedInfo.notes.internal_api_key_hint}</p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {embedInfo && selectedSiteId !== null ? (
        <WidgetCustomizerDialog
          open={isWidgetCustomizerOpen}
          onOpenChange={setIsWidgetCustomizerOpen}
          siteId={selectedSiteId}
          embedInfo={embedInfo}
          onSaved={setEmbedInfo}
        />
      ) : null}

    </>
  );
}

export function DashboardBookingsPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Bookings</CardTitle>
          <CardDescription>Upcoming and past appointments booked through chat.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(['upcoming', 'past', 'all'] as const).map((scope) => (
              <Button
                key={scope}
                type="button"
                size="sm"
                variant={bookingScope === scope ? 'default' : 'outline'}
                onClick={() => setBookingScope(scope)}
              >
                {scope === 'upcoming' ? 'Upcoming' : scope === 'past' ? 'Past' : 'All'}
              </Button>
            ))}
          </div>

          {bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings in this view yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Specialist</th>
                    <th className="px-3 py-2 font-medium">Service</th>
                    <th className="px-3 py-2 font-medium">Price</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Calendar</th>
                    <th className="px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => {
                    const isActive = ['pending', 'confirmed'].includes(booking.status);
                    const customer = booking.lead_name || booking.lead_email || '—';
                    const customerDetail = booking.lead_email && booking.lead_name ? booking.lead_email : '';
                    const customerExtras = formatBookingCustomerExtras(booking);
                    const attributionLines = formatBookingAttribution(booking);
                    return (
                      <tr key={booking.id} className="border-t">
                        <td className="px-3 py-2 align-top">{formatBookingWhen(booking)}</td>
                        <td className="px-3 py-2 align-top">
                          <div>{customer}</div>
                          {customerDetail ? <div className="text-xs text-muted-foreground">{customerDetail}</div> : null}
                          {customerExtras.map((line) => (
                            <div key={line} className="text-xs text-muted-foreground">
                              {line}
                            </div>
                          ))}
                          {attributionLines.map((line) => (
                            <div key={line} className="text-xs text-muted-foreground">
                              {line}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2 align-top">{booking.booked_to || '—'}</td>
                        <td className="px-3 py-2 align-top">{booking.booked_for || '—'}</td>
                        <td className="px-3 py-2 align-top">{booking.booking_price || '—'}</td>
                        <td className="px-3 py-2 align-top capitalize">{booking.status.replace('_', ' ')}</td>
                        <td className="px-3 py-2 align-top">
                          <div>{formatCalendarSyncBadge(booking)}</div>
                          {booking.calendar_sync_error ? (
                            <div className="text-xs text-muted-foreground">{booking.calendar_sync_error}</div>
                          ) : null}
                          {booking.calendar_html_link ? (
                            <a
                              className="text-xs text-primary underline"
                              href={booking.calendar_html_link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open event
                            </a>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {isActive && canEdit ? (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isUpdatingBookingId === booking.id}
                                onClick={() => handleBookingStatusUpdate(booking.id, 'cancelled')}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={isUpdatingBookingId === booking.id}
                                onClick={() => handleBookingStatusUpdate(booking.id, 'no_show')}
                              >
                                No-show
                              </Button>
                              {booking.calendar_sync_status === 'failed' ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  disabled={isUpdatingBookingId === booking.id}
                                  onClick={() => handleRetryCalendarSync(booking.id)}
                                >
                                  Retry sync
                                </Button>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardCalendarPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Calendar &amp; scheduling</CardTitle>
          <CardDescription>
            Connect Google Calendar or Calendly first, then turn on &quot;Allow booking inside chat&quot; on Automation.
            In-chat bookings create real calendar events. Without a connected calendar (target chosen), in-chat slot
            picking is blocked — use a fallback scheduling link instead. Disconnecting calendar turns off in-chat
            booking (and Booking entirely if there is no fallback link). Gmail for confirmation emails is separate
            (Automation → Notifications &amp; CRM).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!calendarConnection?.connected ? (
            <div className="space-y-3">
              <p className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                No calendar connected. You can still enable Booking with a fallback scheduler link. In-chat time slots
                stay locked until you connect Google or Calendly and choose a target.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={!canEditCapabilities || isConnectingCalendar} onClick={() => handleConnectCalendar('google')}>
                  {isConnectingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
                </Button>
                <Button type="button" variant="outline" disabled={!canEditCapabilities || isConnectingCalendar} onClick={() => handleConnectCalendar('calendly')}>
                  Connect Calendly
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm">
                <div>
                  Connected: <strong>{calendarConnection.provider}</strong>
                  {calendarConnection.connected_account ? ` (${calendarConnection.connected_account})` : ''}
                </div>
                {calendarConnection.needs_setup ? (
                  <div className="mt-1 text-amber-600">
                    Choose a target calendar or event type below before enabling in-chat booking.
                  </div>
                ) : null}
                {calendarConnection.last_error ? (
                  <div className="mt-1 text-destructive">{calendarConnection.last_error}</div>
                ) : null}
              </div>
              {calendarOptions.length > 0 ? (
                <div className="space-y-2">
                  <FieldLabel
                    text={calendarConnection.provider === 'google' ? 'Target calendar' : 'Event type'}
                    hint="Where new bookings should be created. Required before Allow booking inside chat can be turned on."
                  />
                  <select
                    className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                    value={calendarTargetId}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setCalendarTargetId(nextId);
                      const match = calendarOptions.find((item) => item.id === nextId);
                      if (match?.scheduling_url) {
                        setCalendarSchedulingUrl(match.scheduling_url);
                      }
                    }}
                    disabled={!canEditCapabilities}
                  >
                    {calendarOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="space-y-2">
                <FieldLabel
                  text="Scheduling link"
                  hint="Public booking URL shown when in-chat booking is off or as a backup."
                />
                <Input
                  value={calendarSchedulingUrl}
                  onChange={(e) => setCalendarSchedulingUrl(e.target.value)}
                  disabled={!canEditCapabilities}
                  placeholder="https://calendly.com/your-team/30min"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={!canEditCapabilities || isSaving} onClick={handleSaveCalendarSettings}>
                  Save calendar settings
                </Button>
                <Button type="button" variant="outline" disabled={!canEditCapabilities} onClick={handleDisconnectCalendar}>
                  Disconnect (turns off in-chat booking)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Disconnecting removes calendar sync and turns off &quot;Allow booking inside chat&quot;. Booking stays
                enabled only if a fallback scheduler link is set. Gmail for visitor emails is unchanged.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardPlanPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Plan & Booking Behaviour</CardTitle>
          <CardDescription>
            Plan tier sets how proactive the assistant is. Capability toggles choose which revenue actions are allowed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            {/* plan_tier — billing/entitlement; only platform admins can change (API mirrors this). */}
            <FieldLabel
              text="Plan tier"
              hint="Your subscription plan. Starter locks booking, lead capture, CRM, and related schedule/catalog setup (chat + optional human handoff). Pro unlocks those capabilities. Only an admin can change this."
            />
            {isPlatformAdmin ? (
              <select
                className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                value={planTier}
                onChange={(e) => {
                  const next = e.target.value;
                  setPlanTier(next);
                  // Downgrade to Starter → clear Pro-only toggles. Keep human_handoff as-is.
                  if (next === 'starter') {
                    setBookingEnabled(false);
                    setLeadCaptureEnabled(false);
                    setCrmSyncEnabled(false);
                    setCanBookInChat(false);
                    setLeadFieldPhone(false);
                    setLeadFieldCompany(false);
                    setLeadFieldAddress(false);
                    setLeadFieldJobTitle(false);
                    setLeadFieldNotes(false);
                    setLeadFieldMarketingConsent(false);
                    setSaveToCrm(false);
                    setAskOutcome(false);
                    setHubspotCreateDeal(false);
                  }
                }}
                disabled={!canEdit}
              >
                {visiblePlanTiers(planTier).map((tier) => (
                  <option key={tier.value} value={tier.value}>
                    {tier.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2">
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-medium capitalize text-primary">
                  {planTier}
                </span>
                <span className="text-xs text-muted-foreground">Contact your provider to change your plan.</span>
              </div>
            )}
          </div>
          {isStarterPlan ? (
            <p className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Starter is chat-first: booking, lead capture, and CRM sync are locked. You can still enable{' '}
              <strong>Human handoff</strong> so visitors who ask for a person notify your team — set a Slack webhook
              under Notifications &amp; CRM.
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* capabilities.booking.enabled */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <FieldLabel
                text="Booking enabled"
                hint="Turns booking intent on for this site. You can enable this before connecting a calendar if you set a fallback scheduler link (Schedule). In-chat time slots still need a connected calendar. Locked off on Starter."
              />
              <Switch
                checked={bookingEnabled}
                onCheckedChange={setBookingEnabled}
                disabled={!canEditCapabilities}
              />
            </div>
            {/* booking_policy.can_book_in_chat — requires ready calendar */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <FieldLabel
                text="Allow booking inside chat"
                hint={
                  calendarConnection?.connected && !calendarConnection.needs_setup
                    ? 'Customers pick a time slot in the widget; the appointment is created on your connected calendar. Requires Booking enabled and a connected calendar (Schedule). Locked on Starter.'
                    : 'Requires a connected Google Calendar or Calendly with a target chosen (Schedule) before you can turn this on. Until then, enable Booking with a fallback scheduler link instead. Disconnecting the calendar turns this off automatically.'
                }
              />
              <Switch
                checked={canBookInChat}
                onCheckedChange={(next) => {
                  if (next && !(calendarConnection?.connected && !calendarConnection.needs_setup)) {
                    return;
                  }
                  setCanBookInChat(next);
                }}
                disabled={
                  !canEditCapabilities ||
                  !(calendarConnection?.connected && !calendarConnection.needs_setup)
                }
              />
            </div>
            {bookingEnabled && !(calendarConnection?.connected && !calendarConnection.needs_setup) ? (
              <p className="md:col-span-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Calendar not ready for in-chat booking. Connect Google or Calendly on Schedule and choose a target,
                then enable &quot;Allow booking inside chat&quot;. Or keep Booking on with a fallback scheduler link
                only. Disconnecting calendar later turns in-chat booking off again (Gmail for emails is separate).
              </p>
            ) : null}
            {/* capabilities.lead_capture.enabled */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <FieldLabel
                text="Lead capture enabled"
                hint="When on, the assistant can ask for contact details (name/email and optional fields) when interest is detected—even without booking a slot. Locked off on Starter."
              />
              <Switch
                checked={leadCaptureEnabled}
                onCheckedChange={setLeadCaptureEnabled}
                disabled={!canEditCapabilities}
              />
            </div>
            {/* capabilities.crm_sync.enabled */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <FieldLabel
                text="CRM sync enabled"
                hint="When on, the assistant may trigger CRM sync actions for qualified conversations (company/budget/pipeline cues). Actual HubSpot/webhook delivery still needs Notifications & CRM configured. Locked off on Starter."
              />
              <Switch
                checked={crmSyncEnabled}
                onCheckedChange={setCrmSyncEnabled}
                disabled={!canEditCapabilities}
              />
            </div>
            {/* capabilities.human_handoff.enabled
                How it works (see api/revenue_service.py: choose_capabilities / run_revenue_actions /
                compose_revenue_next_actions for the full 3-stage flow):
                1) Visitor says things like "talk to a human / agent / support"
                2) Bot replies with your Support contact info (below) and shows an opt-in
                   "contact me" form (name, email, phone, what it's about) — stage
                   human_handoff_requested. Nothing is sent to your team yet.
                3) Only if the visitor fills in and submits that form (stage
                   human_handoff_completed) does run_revenue_actions notify your team via
                   n8n → Slack ("Human handoff requested" + name/contact/issue).
                4) Chat then confirms: "our team will reach out to you soon."
                Needs: this toggle ON + a Slack webhook (Notifications & CRM) so step 3 can
                actually reach your team. Allowed on Starter.
                Contact model: there is NO live takeover of the chat widget — a teammate reads
                the Slack alert and reaches out to the visitor separately (email/phone/call). */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <FieldLabel
                text="Human handoff enabled"
                hint="When a visitor asks for a real person, the bot shows your support contact info and an opt-in form (name/email/phone/what it's about). Only submitting that form notifies your team on Slack — nothing is sent just from asking. Not a live chat takeover; staff follow up outside the widget. Required: Slack webhook under Notifications & CRM."
              />
              <Switch
                checked={humanHandoffEnabled}
                onCheckedChange={setHumanHandoffEnabled}
                disabled={!canEditHandoff}
              />
            </div>
            {humanHandoffEnabled ? (
              <>
                {/* provider_options.human_handoff.contact_info — shown verbatim to the visitor
                    the moment they ask for a person (e.g. "support@x.com / +359 88 123 4567"). */}
                <div className="md:col-span-2 space-y-2">
                  <FieldLabel
                    text="Support contact info (shown to visitor)"
                    hint="Free text shown to the visitor as soon as they ask for a person, e.g. an email, phone number, or hours. Optional, but nice so they always have a direct way to reach you even if they skip the form."
                  />
                  <Input
                    value={humanHandoffContactInfo}
                    onChange={(e) => setHumanHandoffContactInfo(e.target.value)}
                    disabled={!canEditHandoff}
                    placeholder="support@yourbusiness.com · +359 88 123 4567 · Mon–Fri 9–17"
                  />
                </div>
                <div className="md:col-span-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-1">
                  <p>
                    <strong className="text-foreground">Setup:</strong> set a{' '}
                    <strong className="text-foreground">Slack webhook</strong> under Notifications &amp; CRM. That is
                    how your team gets the “Human handoff requested” alert (name, email/phone, and what the visitor
                    typed under “What can we help with?”). Without Slack, the visitor can still fill in the form, but
                    nobody is notified.
                  </p>
                  <p>
                    <strong className="text-foreground">How staff follow up:</strong> this is not a live takeover of
                    the chat. A teammate reads the Slack alert, then emails, calls, or messages the visitor outside
                    the widget using the contact details from the form.
                  </p>
                </div>
              </>
            ) : null}
            {isPlatformAdmin ? (
              <div className="flex items-center justify-between rounded-md border p-3">
                <FieldLabel
                  text="Widget customization (self-serve)"
                  hint="Platform-admin-only switch. When on, this site's owner/editor can open the chat widget designer and customize colors/position themselves. When off, only platform admins can change the widget's appearance."
                />
                <Switch
                  checked={widgetCustomizationEnabled}
                  onCheckedChange={setWidgetCustomizationEnabled}
                  disabled={!canEdit}
                />
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardLeadFieldsPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Lead capture fields</CardTitle>
          <CardDescription>
            Name and email are always collected before booking. Enable extra fields per site when you need them.
            {isStarterPlan ? ' Locked on Starter.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Phone number"
              hint="Ask for a phone number in the in-chat booking form. Useful for salons, clinics, and service businesses that call to confirm."
            />
            <Switch checked={leadFieldPhone} onCheckedChange={setLeadFieldPhone} disabled={!canEditCapabilities} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Company"
              hint="Ask which company the visitor is with. Best for B2B sites where company name matters for qualification."
            />
            <Switch checked={leadFieldCompany} onCheckedChange={setLeadFieldCompany} disabled={!canEditCapabilities} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Address"
              hint="Collect a delivery or visit address. Validation is light on purpose so international formats still work."
            />
            <Switch checked={leadFieldAddress} onCheckedChange={setLeadFieldAddress} disabled={!canEditCapabilities} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Job title"
              hint="Optional role or title field for B2B qualification (e.g. Marketing manager)."
            />
            <Switch
              checked={leadFieldJobTitle}
              onCheckedChange={setLeadFieldJobTitle}
              disabled={!canEditCapabilities}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Notes"
              hint="Optional free-text field for special requests or context (max 500 characters)."
            />
            <Switch checked={leadFieldNotes} onCheckedChange={setLeadFieldNotes} disabled={!canEditCapabilities} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Marketing consent"
              hint="Checkbox the visitor must tick to continue when enabled. Stored with timestamp for compliance."
            />
            <Switch
              checked={leadFieldMarketingConsent}
              onCheckedChange={setLeadFieldMarketingConsent}
              disabled={!canEditCapabilities}
            />
          </div>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardHoursPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Working Hours & Availability</CardTitle>
          <CardDescription>When you accept bookings and how slots are generated.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <FieldLabel text="Timezone" hint="IANA timezone used to show and store slot times, e.g. Europe/Sofia, UTC, America/New_York." />
            <select
              className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50 md:max-w-sm"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              disabled={!canEditCapabilities}
            >
              {!TIMEZONE_OPTIONS.some((opt) => opt.value === timezone) && timezone && (
                <option value={timezone}>{timezone} (custom)</option>
              )}
              {TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <FieldLabel
              text="Weekly schedule"
              hint="Set a different working-hours range per day of the week, or turn a day off entirely (e.g. Mon-Fri 09:00-17:00, Sat 10:00-14:00, Sun off)."
            />
            <div className="space-y-2">
              {WEEK_DAYS.map((day) => {
                const entry = daySchedule[day.value];
                return (
                  <div
                    key={day.value}
                    className={`flex flex-wrap items-center gap-3 rounded-md border p-3 transition-colors ${
                      entry.enabled ? '' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex w-24 items-center gap-2">
                      <Switch
                        checked={entry.enabled}
                        onCheckedChange={(checked) => updateDaySchedule(day.value, 'enabled', checked)}
                        disabled={!canEditCapabilities}
                      />
                      <span className="text-sm font-medium">{day.label}</span>
                    </div>
                    {entry.enabled ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          type="time"
                          value={entry.start}
                          onChange={(e) => updateDaySchedule(day.value, 'start', e.target.value)}
                          disabled={!canEditCapabilities}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={entry.end}
                          onChange={(e) => updateDaySchedule(day.value, 'end', e.target.value)}
                          disabled={!canEditCapabilities}
                          className="w-32"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Closed</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <FieldLabel text="Slot interval (min)" hint="Slots are aligned to this many minutes (e.g. 15 => :00, :15, :30, :45). Minimum 5." />
              <Input value={slotIntervalMinutes} onChange={(e) => setSlotIntervalMinutes(e.target.value)} disabled={!canEditCapabilities} type="number" />
            </div>
            <div className="space-y-2">
              <FieldLabel text="Concurrent capacity" hint="How many bookings can happen at the same time (e.g. number of workers). 1 = one booking per slot." />
              <Input value={concurrentCapacity} onChange={(e) => setConcurrentCapacity(e.target.value)} disabled={!canEditCapabilities} type="number" />
            </div>
            <div className="space-y-2">
              <FieldLabel text="Max suggested slots" hint="How many time options the assistant offers the customer at once." />
              <Input value={maxSuggestedSlots} onChange={(e) => setMaxSuggestedSlots(e.target.value)} disabled={!canEditCapabilities} type="number" />
            </div>
            <div className="space-y-2">
              <FieldLabel text="Gap between suggestions (min)" hint="Minimum spacing between the slots that are suggested, so options aren't all bunched together." />
              <Input value={suggestionGap} onChange={(e) => setSuggestionGap(e.target.value)} disabled={!canEditCapabilities} type="number" />
            </div>
            <div className="space-y-2">
              <FieldLabel text="Search window (days)" hint="How far into the future the assistant looks for free slots." />
              <Input value={searchDays} onChange={(e) => setSearchDays(e.target.value)} disabled={!canEditCapabilities} type="number" />
            </div>
            <div className="space-y-2">
              <FieldLabel text="Scheduling link (fallback)" hint="Used when in-chat booking is off or as backup. Overridden by connected calendar scheduling link when set." />
              <Input value={fallbackUrl} onChange={(e) => setFallbackUrl(e.target.value)} disabled={!canEditCapabilities} placeholder="https://calendly.com/your-team/30min" />
            </div>
          </div>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardSpecialDatesPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
    <details className="group rounded-lg border border-border open:shadow-sm">
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:content-none [&::-webkit-details-marker]:hidden">
        Special dates
        <span className="ml-2 text-xs font-normal text-muted-foreground">(optional — click to expand)</span>
      </summary>
      <div className="border-t border-border p-0">
      <Card>
        <CardHeader>
          <CardTitle>Special Dates (Optional)</CardTitle>
          <CardDescription>
            Override the weekly schedule for a specific calendar date — e.g. shorter hours or fully closed for a
            holiday. Past dates disappear from this list automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {visibleDateOverrides.map(({ row, index }) => (
            <div key={index} className="space-y-3 rounded-md border p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
                <div className="space-y-1">
                  <FieldLabel text="Date" hint="The specific calendar date this override applies to, in your configured timezone." />
                  <Input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateDateOverride(index, 'date', e.target.value)}
                    disabled={!canEditCapabilities}
                  />
                </div>
                <div className="flex items-center gap-2 self-end pb-2">
                  <Switch
                    checked={row.enabled}
                    onCheckedChange={(checked) => updateDateOverride(index, 'enabled', checked)}
                    disabled={!canEditCapabilities}
                  />
                  <span className="text-sm text-muted-foreground">{row.enabled ? 'Open (custom hours)' : 'Closed all day'}</span>
                </div>
                <div className="flex items-end justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!canEditCapabilities}
                    onClick={() => removeDateOverride(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {row.enabled && (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div className="space-y-1">
                    <FieldLabel text="Start" hint="Start time on this date (24h HH:MM)." />
                    <Input
                      type="time"
                      value={row.start}
                      onChange={(e) => updateDateOverride(index, 'start', e.target.value)}
                      disabled={!canEditCapabilities}
                    />
                  </div>
                  <div className="space-y-1">
                    <FieldLabel text="End" hint="End time on this date (24h HH:MM)." />
                    <Input
                      type="time"
                      value={row.end}
                      onChange={(e) => updateDateOverride(index, 'end', e.target.value)}
                      disabled={!canEditCapabilities}
                    />
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <FieldLabel text="Reason (optional)" hint="Internal note only, e.g. 'Public holiday' — not shown to visitors." />
                <Input
                  value={row.reason}
                  onChange={(e) => updateDateOverride(index, 'reason', e.target.value)}
                  disabled={!canEditCapabilities}
                  placeholder="Public holiday"
                />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" disabled={!canEditCapabilities} onClick={addDateOverride}>
            <Plus className="mr-1 h-4 w-4" /> Add date override
          </Button>
        </CardContent>
      </Card>

      </div>
    </details>

    </>
  );
}

export function DashboardBreaksPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
    <details className="group rounded-lg border border-border open:shadow-sm">
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:content-none [&::-webkit-details-marker]:hidden">
        Breaks
        <span className="ml-2 text-xs font-normal text-muted-foreground">(optional — click to expand)</span>
      </summary>
      <div className="border-t border-border p-0">
      <Card>
        <CardHeader>
          <CardTitle>Breaks (Optional)</CardTitle>
          <CardDescription>Add lunch/rest breaks to block booking suggestions during those times.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {breaks.map((row, index) => (
            <div key={index} className="space-y-3 rounded-md border p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
                <div className="space-y-1">
                  <FieldLabel text="Break start" hint="Start time for this break window (HH:MM)." />
                  <Input
                    value={row.start}
                    onChange={(e) => updateBreak(index, 'start', e.target.value)}
                    disabled={!canEditCapabilities}
                    placeholder="12:00"
                  />
                </div>
                <div className="space-y-1">
                  <FieldLabel text="Break end" hint="End time for this break window (HH:MM)." />
                  <Input
                    value={row.end}
                    onChange={(e) => updateBreak(index, 'end', e.target.value)}
                    disabled={!canEditCapabilities}
                    placeholder="13:00"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!canEditCapabilities}
                    onClick={() => setBreaks((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <FieldLabel text="Applies on days" hint="Leave all days selected to apply the break daily, or limit it to specific weekdays." />
                <div className="flex flex-wrap gap-2">
                  {WEEK_DAYS.map((day) => {
                    const active = row.days.includes(day.value);
                    return (
                      <button
                        key={`${index}-${day.value}`}
                        type="button"
                        disabled={!canEditCapabilities}
                        onClick={() => toggleBreakDay(index, day.value)}
                        className={`rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ${
                          active ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground/70'
                        }`}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEditCapabilities}
            onClick={() => setBreaks((prev) => [...prev, { start: '12:00', end: '13:00', days: [...enabledWeekDays] }])}
          >
            <Plus className="mr-1 h-4 w-4" /> Add break
          </Button>
        </CardContent>
      </Card>

      </div>
    </details>

    </>
  );
}

export function DashboardTimeOffPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
    <details className="group rounded-lg border border-border open:shadow-sm">
      <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:content-none [&::-webkit-details-marker]:hidden">
        Worker time-off
        <span className="ml-2 text-xs font-normal text-muted-foreground">(optional — click to expand)</span>
      </summary>
      <div className="border-t border-border p-0">
      <Card>
        <CardHeader>
          <CardTitle>Worker Time-Off (Optional)</CardTitle>
          <CardDescription>Block selected workers for vacation/sick leave or mark all workers unavailable.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {unavailableWindows.map((row, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border p-3 md:grid-cols-[1fr_1fr_1fr_1fr_auto]">
              <div className="space-y-1">
                <FieldLabel text="Worker" hint="Choose a worker to block. Leave as All workers to block the whole business." />
                <select
                  className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                  value={row.resource_key}
                  onChange={(e) => updateUnavailableWindow(index, 'resource_key', e.target.value)}
                  disabled={!canEditCapabilities}
                >
                  <option value="">All workers</option>
                  {assigneeOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <FieldLabel text="Start" hint="Start date/time (local timezone) when worker is unavailable." />
                <Input
                  type="datetime-local"
                  value={row.start_at_local}
                  onChange={(e) => updateUnavailableWindow(index, 'start_at_local', e.target.value)}
                  disabled={!canEditCapabilities}
                />
              </div>
              <div className="space-y-1">
                <FieldLabel text="End" hint="End date/time (local timezone) when worker becomes available again." />
                <Input
                  type="datetime-local"
                  value={row.end_at_local}
                  onChange={(e) => updateUnavailableWindow(index, 'end_at_local', e.target.value)}
                  disabled={!canEditCapabilities}
                />
              </div>
              <div className="space-y-1">
                <FieldLabel text="Reason" hint="Optional note for admins (e.g. Vacation, Training)." />
                <Input
                  value={row.reason}
                  onChange={(e) => updateUnavailableWindow(index, 'reason', e.target.value)}
                  disabled={!canEditCapabilities}
                  placeholder="Vacation"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!canEditCapabilities}
                  onClick={() => setUnavailableWindows((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEditCapabilities}
            onClick={() =>
              setUnavailableWindows((prev) => [
                ...prev,
                { resource_key: '', start_at_local: '', end_at_local: '', reason: '' },
              ])
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add time-off window
          </Button>
        </CardContent>
      </Card>

      </div>
    </details>

    </>
  );
}

export function DashboardSpecialistsPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Specialists</CardTitle>
          <CardDescription>People/resources customers can be booked with (e.g. Kandy for hair). Leave empty if you don't offer a choice.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {assignees.map((row, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={row.name}
                onChange={(e) => updateAssignee(index, e.target.value)}
                disabled={!canEditCapabilities}
                placeholder="Specialist name"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={!canEditCapabilities}
                onClick={() => setAssignees((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEditCapabilities}
            onClick={() =>
              setAssignees((prev) => [
                ...prev,
                { id: `resource-${prev.length + 1}`, name: '' },
              ])
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add specialist
          </Button>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardServicesPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Services</CardTitle>
          <CardDescription>Service types and how long each takes. Duration controls how long the slot/calendar event is.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {services.map((row, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border p-3 md:grid-cols-[1fr_140px_1fr_auto]">
              <div className="space-y-1">
                <FieldLabel text="Name" hint="Service name shown to the customer, e.g. Haircut, Demo call." />
                <Input value={row.name} onChange={(e) => updateService(index, 'name', e.target.value)} disabled={!canEditCapabilities} placeholder="Service name" />
              </div>
              <div className="space-y-1">
                <FieldLabel text="Minutes" hint="Length of this service in minutes. Sets slot length and the calendar event duration." />
                <Input value={row.duration_minutes} onChange={(e) => updateService(index, 'duration_minutes', e.target.value)} disabled={!canEditCapabilities} type="number" placeholder="30" />
              </div>
              <div className="space-y-1">
                <FieldLabel text="Booking URL (optional)" hint="Service-specific scheduler link (e.g. a Calendly link for this exact duration). Falls back to the general scheduler URL if empty." />
                <Input value={row.booking_url} onChange={(e) => updateService(index, 'booking_url', e.target.value)} disabled={!canEditCapabilities} placeholder="https://calendly.com/..." />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!canEditCapabilities}
                  onClick={() => setServices((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEditCapabilities}
            onClick={() => setServices((prev) => [...prev, { name: '', duration_minutes: '', booking_url: '' }])}
          >
            <Plus className="mr-1 h-4 w-4" /> Add service
          </Button>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardPricingPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Service pricing</CardTitle>
          <CardDescription>
            Optional prices per procedure and specialist. Shown in chat when the customer picks a combination, and sent
            to HubSpot / CRM webhooks on booking.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <FieldLabel
              text="Default currency"
              hint="Currency used for all price rows unless you add per-row currency support later."
            />
            <select
              className="w-full max-w-xs rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
              value={priceCurrency}
              onChange={(e) => setPriceCurrency(e.target.value)}
              disabled={!canEditCapabilities}
            >
              {PRICE_CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
          {servicePricing.map((row, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 rounded-md border p-3 md:grid-cols-[1fr_1fr_140px_auto]">
              <div className="space-y-1">
                <FieldLabel text="Procedure" hint="Must match a service name above." />
                <select
                  className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                  value={row.service}
                  onChange={(e) => updateServicePrice(index, 'service', e.target.value)}
                  disabled={!canEditCapabilities}
                >
                  <option value="">Select procedure</option>
                  {serviceNameOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <FieldLabel text="Specialist" hint="Leave as Any specialist for a default price for that procedure." />
                <select
                  className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                  value={row.assignee}
                  onChange={(e) => updateServicePrice(index, 'assignee', e.target.value)}
                  disabled={!canEditCapabilities}
                >
                  <option value="">Any specialist</option>
                  {assigneeNameOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <FieldLabel text="Price" hint={`Amount in ${priceCurrency}.`} />
                <Input
                  value={row.amount}
                  onChange={(e) => updateServicePrice(index, 'amount', e.target.value)}
                  disabled={!canEditCapabilities}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="45"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!canEditCapabilities}
                  onClick={() => setServicePricing((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEditCapabilities}
            onClick={() =>
              setServicePricing((prev) => [
                ...prev,
                { service: serviceNameOptions[0] || '', assignee: '', amount: '' },
              ])
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add price
          </Button>
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardCrmPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail,
    customerEmailFrom, setCustomerEmailFrom, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    emailConnection, isConnectingEmail, handleConnectEmail, handleDisconnectEmail,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Notifications & CRM</CardTitle>
          <CardDescription>
            Team alerts (email/Slack) for bookings, plus optional CRM sync. Human handoff alerts are delivered via
            the Slack webhook below (set it if you use Human handoff). HubSpot uses the CRM API; other systems use a
            webhook your automation can forward to (Zapier, Make, Salesforce, Pipedrive, etc.).
            {isStarterPlan
              ? ' On Starter, booking/lead/CRM notification fields are locked — only the human-handoff Slack webhook stays editable.'
              : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border p-4 space-y-3">
            <FieldLabel
              text="Send booking emails from your Gmail"
              hint="Connect Gmail so visitor confirmation, reminder, and outcome emails are sent from your Google account (true From). Without it, emails fall back to EmbedReply delivery with Reply-To set below. Disconnecting Gmail does not turn off Booking or your calendar — only email delivery changes. Locked on Starter."
            />
            {emailConnection?.connected ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Sending as <strong className="text-foreground">{emailConnection.connected_account || 'Gmail'}</strong>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canEditCapabilities || isConnectingEmail}
                    onClick={() => void handleDisconnectEmail()}
                  >
                    Disconnect Gmail (keeps booking on)
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Separate from calendar. Disconnecting only changes who sends confirmation emails — Booking and
                  in-chat slots stay as configured.
                </p>
              </div>
            ) : (
              <Button
                type="button"
                disabled={!canEditCapabilities || isConnectingEmail}
                onClick={() => void handleConnectEmail()}
              >
                {isConnectingEmail ? 'Connecting…' : 'Connect Gmail'}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <FieldLabel
                text="Customer booking emails from"
                hint={
                  emailConnection?.connected
                    ? 'Synced from your connected Gmail. Used as Reply-To only if Gmail is disconnected (Resend fallback). Locked on Starter.'
                    : 'Reply-To for visitor booking emails when Gmail is not connected (From stays EmbedReply). Defaults to the site owner. Connect Gmail above to send as your own address. Locked on Starter.'
                }
              />
              <Input
                value={customerEmailFrom}
                onChange={(e) => setCustomerEmailFrom(e.target.value)}
                disabled={!canEditCapabilities || Boolean(emailConnection?.connected)}
                type="email"
                placeholder="owner@yourbusiness.com"
              />
            </div>
            <div className="space-y-2">
              <FieldLabel
                text="Team email"
                hint="Email used for booking-related team notifications via n8n (e.g. incomplete-booking follow-ups). Locked on Starter."
              />
              <Input value={teamEmail} onChange={(e) => setTeamEmail(e.target.value)} disabled={!canEditCapabilities} type="email" placeholder="team@yourbusiness.com" />
            </div>
            <div className="space-y-2">
              <FieldLabel
                text="Slack webhook URL (general)"
                hint="Incoming Slack webhook for booking / lead-capture / CRM alerts. Human-handoff alerts use the dedicated webhook below, not this one. Locked on Starter."
              />
              <Input value={slackWebhook} onChange={(e) => setSlackWebhook(e.target.value)} disabled={!canEditCapabilities} placeholder="https://hooks.slack.com/services/..." />
            </div>
            <div className="space-y-2">
              {/* post_booking.human_handoff_slack_webhook_url — dedicated channel for
                  human_handoff alerts only (stage human_handoff_completed), separate from
                  the general channel above and the negative-feedback channel below. */}
              <FieldLabel
                text="Human handoff Slack webhook URL"
                hint="Required for human-handoff Slack alerts. Separate incoming webhook for “Human handoff requested” only (name, contact, issue). Does not use the general Slack webhook above. Allowed on Starter."
              />
              <Input
                value={humanHandoffSlackWebhook}
                onChange={(e) => setHumanHandoffSlackWebhook(e.target.value)}
                disabled={!canEditHandoff}
                placeholder="https://hooks.slack.com/services/..."
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Ask for outcome after appointment"
              hint="Sends a follow-up email asking if the appointment went well. Yes links to your Google review page; No collects feedback and posts it to Slack. Locked on Starter."
            />
            <Switch checked={askOutcome} onCheckedChange={setAskOutcome} disabled={!canEditCapabilities} />
          </div>
          {askOutcome ? (
            <div className="grid grid-cols-1 gap-4 rounded-md border p-4 md:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel
                  text="Google review URL"
                  hint="Link to your Google Business review page (Google Maps → Share → Reviews). Shown to leads who report a positive outcome."
                />
                <Input
                  value={googleReviewUrl}
                  onChange={(e) => setGoogleReviewUrl(e.target.value)}
                  disabled={!canEditCapabilities}
                  placeholder="https://g.page/r/.../review"
                />
              </div>
              <div className="space-y-2">
                <FieldLabel
                  text="Negative-feedback Slack webhook URL"
                  hint="Separate incoming Slack webhook pointed at your #negative-feedback channel. Negative outcome feedback is posted here."
                />
                <Input
                  value={negativeFeedbackSlackWebhook}
                  onChange={(e) => setNegativeFeedbackSlackWebhook(e.target.value)}
                  disabled={!canEditCapabilities}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </div>
            </div>
          ) : null}
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Run post-booking automations"
              hint="Required for CRM sync, Slack alerts, confirmation emails, and calendar follow-ups after a completed booking. Also used when Human handoff notifies your team. If this is off, bookings still save in the dashboard but CRM and team notifications will not run."
            />
            <Switch checked={automationEnabled} onCheckedChange={setAutomationEnabled} disabled={!canEdit} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <FieldLabel
              text="Sync leads to CRM"
              hint="When enabled, completed leads and bookings are sent to the CRM provider you select below. Requires “Run post-booking automations” above. Locked on Starter."
            />
            <Switch
              checked={saveToCrm}
              onCheckedChange={(next) => {
                setSaveToCrm(next);
                if (next && (crmProvider === 'none' || !crmProvider)) {
                  setCrmProvider('hubspot');
                }
              }}
              disabled={!canEditCapabilities}
            />
          </div>
          {saveToCrm ? (
            <div className="space-y-4 rounded-md border p-4">
              <div className="space-y-2">
                <FieldLabel
                  text="CRM provider"
                  hint="HubSpot: paste an access token from HubSpot Development (legacy app or new platform static token). Webhook: any HTTPS endpoint that accepts JSON lead payloads."
                />
                <select
                  className="w-full rounded-md border border-input bg-background p-2 text-foreground disabled:opacity-50"
                  value={crmProvider === 'webhook' ? 'webhook' : 'hubspot'}
                  onChange={(e) => setCrmProvider(e.target.value as CrmProvider)}
                  disabled={!canEditCapabilities}
                >
                  {CRM_PROVIDERS.filter((p) => p.value !== 'none').map((provider) => (
                    <option key={provider.value} value={provider.value}>
                      {provider.label}
                    </option>
                  ))}
                </select>
              </div>
              {crmProvider !== 'webhook' ? (
                <div className="space-y-2">
                  <FieldLabel
                    text="HubSpot access token"
                    hint="From HubSpot → Development → Private/Legacy app. Scopes: crm.objects.contacts.read, crm.objects.contacts.write, crm.objects.meetings.write; add crm.objects.deals.write for Pro deal creation. Re-import the n8n workflow after updates. Create custom contact properties prefixed with supachat_ to see attribution/booking fields on the contact."
                  />
                  <PasswordInput
                    value={hubspotAccessToken}
                    onChange={(e) => setHubspotAccessToken(e.target.value)}
                    disabled={!canEditCapabilities}
                    placeholder="pat-... or static platform token"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    Create custom contact properties prefixed with <code>supachat_</code> in HubSpot (or they may
                    auto-create depending on your portal).
                  </p>
                  {planTier === 'pro' ? (
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <FieldLabel
                        text="Create HubSpot deal on booking"
                        hint="Pro only. Creates a deal in appointmentscheduled stage and associates it with the contact."
                      />
                      <Switch checked={hubspotCreateDeal} onCheckedChange={setHubspotCreateDeal} disabled={!canEditCapabilities} />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {crmProvider === 'webhook' ? (
                <>
                  <div className="space-y-2">
                    <FieldLabel
                      text="CRM webhook URL"
                      hint="POST endpoint for lead/booking JSON. Use Zapier/Make catch hook, your API, or a second n8n workflow."
                    />
                    <Input
                      value={crmWebhook}
                      onChange={(e) => setCrmWebhook(e.target.value)}
                      disabled={!canEditCapabilities}
                      placeholder="https://hooks.zapier.com/... or https://api.example.com/crm/leads"
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      text="Webhook auth token (optional)"
                      hint="If set, sent as Authorization: Bearer &lt;token&gt; on CRM webhook requests."
                    />
                    <PasswordInput
                      value={crmAuthToken}
                      onChange={(e) => setCrmAuthToken(e.target.value)}
                      disabled={!canEditCapabilities}
                      placeholder="Optional bearer token"
                      autoComplete="off"
                    />
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

    </>
  );
}

export function DashboardAdvancedPanel() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      {isPlatformAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Advanced (admin only)</CardTitle>
            <CardDescription>Raw config JSON. Edits here are merged on save and override matching form fields.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea rows={14} value={advancedConfigJson} onChange={(e) => setAdvancedConfigJson(e.target.value)} disabled={!canEdit} />
          </CardContent>
        </Card>
      ) : null}

    </>
  );
}

export function DashboardSaveBar() {
  const {

    email, setEmail, password, setPassword,
    inviteToken, setInviteToken, inviteName, setInviteName, invitePassword, setInvitePassword,
    authView, setAuthView,
    forgotEmail, setForgotEmail, forgotSubmitted, setForgotSubmitted, isForgotLoading,
    resetToken, setResetToken, resetNewPassword, setResetNewPassword,
    resetConfirmPassword, setResetConfirmPassword, isResettingPassword,
    isAuthenticated, isPlatformAdmin, mustResetPassword,
    handleLogin, handleAcceptInvite, handleForgotPassword, handleResetPassword, handleLogout,
    handleChangePassword,
    currentPassword, setCurrentPassword, newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    isChangingPassword, isSecurityOpen, setIsSecurityOpen,
    sites, selectedSiteId, setSelectedSiteId, selectedSite, selectedSiteRole, config, canEdit,
    canEditCapabilities, canEditHandoff, isStarterPlan, canCustomizeWidget,
    defaultLanguage, setDefaultLanguage,
    insights, billing, setBilling, bookings, bookingScope, setBookingScope,
    calendarConnection, calendarOptions, calendarTargetId, setCalendarTargetId,
    calendarSchedulingUrl, setCalendarSchedulingUrl, embedInfo, setEmbedInfo,
    isSaving, isConnectingCalendar, isUpdatingBookingId,
    isWidgetCustomizerOpen, setIsWidgetCustomizerOpen,
    planTier, setPlanTier,
    bookingEnabled, setBookingEnabled, leadCaptureEnabled, setLeadCaptureEnabled,
    crmSyncEnabled, setCrmSyncEnabled, humanHandoffEnabled, setHumanHandoffEnabled,
    humanHandoffContactInfo, setHumanHandoffContactInfo,
    widgetCustomizationEnabled, setWidgetCustomizationEnabled,
    canBookInChat, setCanBookInChat,
    leadFieldPhone, setLeadFieldPhone, leadFieldCompany, setLeadFieldCompany,
    leadFieldAddress, setLeadFieldAddress, leadFieldJobTitle, setLeadFieldJobTitle,
    leadFieldNotes, setLeadFieldNotes, leadFieldMarketingConsent, setLeadFieldMarketingConsent,
    hubspotCreateDeal, setHubspotCreateDeal,
    timezone, setTimezone, daySchedule, dateOverrides,
    slotIntervalMinutes, setSlotIntervalMinutes, concurrentCapacity, setConcurrentCapacity,
    maxSuggestedSlots, setMaxSuggestedSlots, suggestionGap, setSuggestionGap,
    searchDays, setSearchDays, fallbackUrl, setFallbackUrl,
    assignees, setAssignees, services, setServices, servicePricing, setServicePricing,
    priceCurrency, setPriceCurrency, breaks, setBreaks, unavailableWindows, setUnavailableWindows,
    slackWebhook, setSlackWebhook, teamEmail, setTeamEmail, askOutcome, setAskOutcome,
    googleReviewUrl, setGoogleReviewUrl, negativeFeedbackSlackWebhook, setNegativeFeedbackSlackWebhook,
    humanHandoffSlackWebhook, setHumanHandoffSlackWebhook,
    saveToCrm, setSaveToCrm, automationEnabled, setAutomationEnabled,
    crmProvider, setCrmProvider, crmWebhook, setCrmWebhook, crmAuthToken, setCrmAuthToken,
    hubspotAccessToken, setHubspotAccessToken, advancedConfigJson, setAdvancedConfigJson,
    enabledWeekDays, visibleDateOverrides, assigneeOptions, serviceNameOptions, assigneeNameOptions,
    updateDaySchedule, addDateOverride, updateDateOverride, removeDateOverride,
    updateAssignee, updateService, updateServicePrice, updateBreak, toggleBreakDay, updateUnavailableWindow,
    handleSaveConfig, handleBookingStatusUpdate, handleRetryCalendarSync,
    handleConnectCalendar, handleDisconnectCalendar, handleSaveCalendarSettings,
    copyText, formatBookingWhen, formatBookingCustomerExtras, formatBookingAttribution, formatCalendarSyncBadge,
    refreshBilling, loadSiteData, isDirty, markSavedBaseline

  } = useD();

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={handleSaveConfig} disabled={!canEdit || isSaving}>
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </div>

    </>
  );
}

export function DashboardBillingSection() {
  const { selectedSiteId, billing, isPlatformAdmin, refreshBilling } = useD();
  return (
    <div className="space-y-4">
      <DashboardBillingCard
        siteId={selectedSiteId}
        billing={billing}
        isPlatformAdmin={isPlatformAdmin}
        onRefresh={refreshBilling}
      />
      <DashboardChatbotRuntimeCard
        siteId={selectedSiteId}
        billing={billing}
        onRefresh={refreshBilling}
      />
    </div>
  );
}

export function DashboardInsightsSection() {
  const {
    insights, bookingEnabled, leadCaptureEnabled, humanHandoffEnabled, askOutcome, isPlatformAdmin,
  } = useD();
  if (!insights) return null;
  return (
    <DashboardInsightsCards
      insights={insights}
      bookingEnabled={bookingEnabled}
      leadCaptureEnabled={leadCaptureEnabled}
      humanHandoffEnabled={humanHandoffEnabled}
      askOutcomeEnabled={askOutcome}
      isPlatformAdmin={isPlatformAdmin}
    />
  );
}
